import { call, put, select } from "typed-redux-saga";
import { v4 as uuidv4 } from "uuid";
import { AppConfig } from "../../AppConfig";
import { MembershipResponse } from "../../core/api/requests/auth/membership/types";
import { CouponCheckResponse } from "../../core/api/requests/coupon/check/types";
import { CouponExternalResponse } from "../../core/api/requests/coupon/external/types";
import { CouponLimitedResponse } from "../../core/api/requests/coupon/limited/types";
import { CouponRedeemResponse } from "../../core/api/requests/coupon/redeem/types";
import { CouponSearchResponse } from "../../core/api/requests/coupon/search/types";
import { CouponValidateResponse } from "../../core/api/requests/coupon/validate/types";
import { MenuLimitedResponse } from "../../core/api/requests/menu/limited/types";
import {
  couponCheckApi,
  couponExternalApi,
  couponLimitedApi,
  couponRedeemApi,
  couponSearchApi,
  couponValidateApi,
  membershipApi,
  menuLimitedApi,
} from "../../core/http/httpClient";
import { LocalStorageManager } from "../../core/storage/LocalStorageManager";
import { ApiResponse } from "../../core/types";
import { MenuInCart } from "../menu";
import { takeEveryFsa } from "../operations";
import { couponActions } from "./actions";
import {
  AppCoupon,
  AppliedCoupon,
  Coupon,
  CouponExternal,
  CouponGroupExternal,
} from "./types";

export function* couponSaga() {
  yield takeEveryFsa(couponActions.getCoupons.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<CouponSearchResponse> = yield call(
      () => {
        return couponSearchApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      couponActions.getCoupons.done({
        params: action.payload,
        result: response.data.coupons.map<Coupon>((coupon) => ({
          id: coupon.id,
          code: coupon.code,
          name: coupon.name,
          discount: coupon.discount_amount,
          discountRate: coupon.discount_rate,
          imageUrl: coupon.image,
          expirationDate: new Date(coupon.end_available_date),
          isFreeCoupon: coupon.is_free_coupon === 1,
        })),
      }),
    );
    onSuccess?.(response.data);
  });

  yield takeEveryFsa(
    couponActions.getCouponExternals.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<CouponExternalResponse> =
        yield call(() => {
          return couponExternalApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        });

      onComplete?.();

      if (!response || error) return onError?.(error);
      const result = {
        remainingSlot: response.data.remaining_slots,
        couponGroups: response.data.coupon_groups.map<CouponGroupExternal>(
          (couponGroup) => ({
            parentCode: couponGroup.parent_code,
            name: couponGroup.name,
            imageUrl: couponGroup.image_url,
            endAvailableDate: couponGroup.end_available_date,
            coupons: couponGroup.coupons.map<CouponExternal>((coupon) => ({
              code: coupon.code,
              kind: coupon.kind,
              name: coupon.name,
              imageUrl: coupon.image_url ?? "",
              isFreeCoupon: coupon.is_free_coupon === 1,
              isTotalCoupon: !coupon.menu,
              menu: coupon.menu
                ? {
                    id: coupon.menu.id,
                    name: coupon.menu.name,
                    code: coupon.menu.code,
                    imageUrl: coupon.menu.image_url ?? "",
                    price: coupon.menu.price,
                    priceWithTax: coupon.menu.price_include_tax,
                  }
                : null,
            })),
          }),
        ),
      };
      yield* put(
        couponActions.getCouponExternals.done({
          params: action.payload,
          result,
        }),
      );
      onSuccess?.(result);
    },
  );

  yield takeEveryFsa(
    couponActions.getAppliedCoupons.started,
    function* (action) {
      const appliedCoupons: AppliedCoupon[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        ) ?? [];
      const unselectedAppCoupons: string[] | null =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
        ) ?? null;
      const selectedAppCoupons: AppCoupon[] | null =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        ) ?? null;

      yield* put(
        couponActions.getAppliedCoupons.done({
          params: action.payload,
          result: {
            appliedCoupons,
            unselectedAppCoupons,
            selectedAppCoupons,
          },
        }),
      );
    },
  );

  yield takeEveryFsa(
    couponActions.removeAppliedCoupon.started,
    function* (action) {
      const { onComplete, onError, onSuccess, isDeleteOnce, ...body } =
        action.payload;

      let appliedCoupons: AppliedCoupon[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        ) ?? [];

      appliedCoupons = isDeleteOnce
        ? removeOnePerUuid(appliedCoupons, body.couponUuids)
        : appliedCoupons.filter(
            (coupon) => !body.couponUuids.includes(coupon.uuid),
          );

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        appliedCoupons,
      );

      yield* put(
        couponActions.removeAppliedCoupon.done({
          params: action.payload,
          result: appliedCoupons,
        }),
      );
      onSuccess?.({});
    },
  );

  yield takeEveryFsa(couponActions.validateCoupon.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<CouponValidateResponse> = yield call(
      () => {
        return couponValidateApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      couponActions.validateCoupon.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.({
      success: [...response.data.coupon_code, ...response.data.extra_coupon],
      errors: [
        ...response.data.errors.coupon_code,
        ...response.data.errors.extra_coupon,
        //...response.data.errors.coupon_code.map((coupon) => coupon.coupon_code),
        /*...response.data.errors.extra_coupon.map(
          (coupon) => coupon.coupon_code,
        ),*/
      ],
    });
  });

  yield takeEveryFsa(couponActions.applyCoupon.started, function* (action) {
    const {
      onComplete,
      onError,
      onSuccess,
      isAppCoupon,
      menuExternalCoupons,
      ...body
    } = action.payload;

    const { response, error }: ApiResponse<MenuLimitedResponse> = yield call(
      () => {
        return menuLimitedApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    const appliedCoupons: AppliedCoupon[] =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["appliedCoupons"],
      ) ?? [];
    const couponMenus: MenuInCart[] = [];
    const isTotalCoupon = response.data.menus.length === 0;
    if (isTotalCoupon) {
      action.payload.coupon_code.forEach((couponCode) => {
        if (appliedCoupons.find((coupon) => coupon.code === couponCode)) return;
        const couponUuid = uuidv4();
        appliedCoupons.push({
          uuid: couponUuid,
          code: couponCode,
          isAppCoupon: isAppCoupon ?? false,
          isTotalCoupon: true,
          isFreeCoupon: false,
        });
      });
    } else {
      const newAppliedCoupons: AppliedCoupon[] = [];
      response.data.menus.forEach((menu) => {
        const couponUuid = uuidv4();
        let quantity = 1;
        // If apply from coupon list page
        const menuExternalCoupon = menuExternalCoupons?.find(
          (menuExternalCoupon) =>
            menuExternalCoupon.id === menu.id &&
            menuExternalCoupon.couponCode === menu.coupon[0]?.code,
        );
        if (menuExternalCoupon) {
          quantity = menuExternalCoupon.quantity;
        }

        couponMenus.push({
          menuId: menu.id,
          menuName: menu.name,
          amount: quantity,
          couponCode: menu.coupon[0]?.code ?? "",
          isEarlyBird: false,
          couponUuid: couponUuid,
        });
        newAppliedCoupons.push({
          uuid: couponUuid,
          code: menu.coupon[0]?.code ?? "",
          menuName: menu.name,
          menuCode: menu.code,
          isAppCoupon: isAppCoupon ?? false,
          isTotalCoupon: false,
          isFreeCoupon: menu.coupon[0]?.is_free_coupon === 1,
        });
      });
      appliedCoupons.push(...newAppliedCoupons);
    }

    yield* put(
      couponActions.applyCoupon.done({
        params: action.payload,
        result: {},
      }),
    );
    onSuccess?.({ couponMenus, appliedCoupons });
  });

  yield takeEveryFsa(couponActions.redeemCoupon.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<CouponRedeemResponse> = yield call(
      () =>
        couponRedeemApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error })),
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    const unselectedAppCoupons: string[] | null =
      LocalStorageManager.Instance.getObject(
        AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
      ) ?? null;
    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
      unselectedAppCoupons === null
        ? response.data.coupons
        : [
            ...unselectedAppCoupons.filter(
              (coupon) => !response.data.coupons.includes(coupon),
            ),
            ...response.data.coupons,
          ],
    );
    yield* put(
      couponActions.redeemCoupon.done({
        params: action.payload,
        result: response.data.coupons,
      }),
    );
    onSuccess?.(response.data.coupons);
  });

  yield takeEveryFsa(
    couponActions.checkAppliedCouponMenu.started,
    function* (action) {
      const {
        onComplete,
        onError,
        onSuccess,
        couponMenus,
        appliedCoupons,
        isAppCoupon,
        ...body
      } = action.payload;

      const { response, error }: ApiResponse<CouponCheckResponse> = yield call(
        () => {
          return couponCheckApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        },
      );

      onComplete?.();

      if (!response || error) return onError?.(error);

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        appliedCoupons,
      );

      if (isAppCoupon) {
        LocalStorageManager.Instance.removeObject(
          AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
        );
        LocalStorageManager.Instance.removeObject(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        );
      }

      yield* put(
        couponActions.checkAppliedCouponMenu.done({
          params: action.payload,
          result: appliedCoupons,
        }),
      );
      onSuccess?.(couponMenus);
    },
  );

  yield takeEveryFsa(
    couponActions.getLimitedCoupons.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;

      const { response, error }: ApiResponse<CouponLimitedResponse> =
        yield call(() => {
          return couponLimitedApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        });

      onComplete?.();

      if (!response || error) return onError?.(error);

      const result = response.data.coupons.map<AppCoupon>((coupon) => ({
        code: coupon.code,
        name: coupon.name,
        menuCode: coupon.menu_code,
        isFreeCoupon: coupon.is_free_coupon === 1,
        kind: coupon.kind,
        isExclusive: coupon.is_exclusive === 1,
      }));

      yield* put(
        couponActions.getLimitedCoupons.done({
          params: action.payload,
          result,
        }),
      );
      onSuccess?.(result);
    },
  );

  yield takeEveryFsa(
    couponActions.saveSelectedAppCoupon.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;

      const appCouponKey: string | null = yield* select(
        (state) => state.coupon.appCouponKey,
      );
      const unselectedAppCoupons: string[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
        ) ?? [];
      const newUnselectedAppCoupons = (() => {
        const idx = unselectedAppCoupons.indexOf(appCouponKey ?? "");
        if (idx === -1) return [...unselectedAppCoupons];
        return [
          ...unselectedAppCoupons.slice(0, idx),
          ...unselectedAppCoupons.slice(idx + 1),
        ];
      })();
      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
        newUnselectedAppCoupons,
      );

      const selectedAppCoupons: AppCoupon[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        ) ?? [];

      // Allow duplicates - always add the coupon code without filtering
      const newSelectedAppCoupons = [...selectedAppCoupons, body.coupon];

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        newSelectedAppCoupons,
      );

      yield* put(
        couponActions.saveSelectedAppCoupon.done({
          params: action.payload,
          result: {
            selectedAppCoupons: newSelectedAppCoupons,
            unselectedAppCoupons: newUnselectedAppCoupons,
          },
        }),
      );
      onSuccess?.(newUnselectedAppCoupons);
    },
  );

  yield takeEveryFsa(
    couponActions.removeSelectedAppCoupon.started,
    function* (action) {
      const { couponCodes, isDeleteOnce } = action.payload;

      const selectedAppCoupons: AppCoupon[] =
        LocalStorageManager.Instance.getObject<AppCoupon[]>(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        ) ?? [];

      const newSelectedAppCoupons = isDeleteOnce
        ? removeOnePerCode(selectedAppCoupons, couponCodes)
        : selectedAppCoupons.filter(
            (coupon) => !couponCodes.includes(coupon.code),
          );

      if (newSelectedAppCoupons.length === 0) {
        LocalStorageManager.Instance.removeObject(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
        );
      } else {
        LocalStorageManager.Instance.saveObject(
          AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
          newSelectedAppCoupons,
        );
      }

      yield* put(
        couponActions.removeSelectedAppCoupon.done({
          params: action.payload,
          result:
            newSelectedAppCoupons.length === 0 ? null : newSelectedAppCoupons,
        }),
      );
    },
  );

  yield takeEveryFsa(
    couponActions.removeAllAppCoupons.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;

      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"],
      );
      LocalStorageManager.Instance.removeObject(
        AppConfig.Instance.LocalStorageKey["selectedAppCoupons"],
      );

      const appliedCoupons: AppliedCoupon[] =
        LocalStorageManager.Instance.getObject(
          AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        ) ?? [];

      const newAppliedCoupons = appliedCoupons.filter(
        (coupon: AppliedCoupon) => !coupon.isAppCoupon,
      );

      LocalStorageManager.Instance.saveObject(
        AppConfig.Instance.LocalStorageKey["appliedCoupons"],
        newAppliedCoupons,
      );

      yield* put(
        couponActions.removeAllAppCoupons.done({
          params: action.payload,
          result: {},
        }),
      );
      onSuccess?.(
        appliedCoupons
          .filter((coupon) => coupon.isAppCoupon)
          .map((coupon) => coupon.code),
      );
    },
  );

  yield takeEveryFsa(couponActions.clearCoupons.started, function* () {
    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["appliedCoupons"],
    );

    yield* put(
      couponActions.clearCoupons.done({
        params: {},
        result: {},
      }),
    );
  });

  yield takeEveryFsa(
    couponActions.linkAppMembership.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<MembershipResponse> = yield call(
        () => {
          return membershipApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        },
      );

      onComplete?.();

      if (!response || error) return onError?.(error);

      yield* put(
        couponActions.linkAppMembership.done({
          params: action.payload,
          result: { appToken: response.data.app_token },
        }),
      );
      onSuccess?.({ appToken: response.data.app_token });
    },
  );
}
const removeOnePerCode = (
  coupons: AppCoupon[],
  targetCodes: string[],
): AppCoupon[] => {
  let result = [...coupons]; // コピー

  for (const code of targetCodes) {
    const index = result.findIndex((coupon) => coupon.code === code);

    if (index !== -1) {
      result = [...result.slice(0, index), ...result.slice(index + 1)];
    }
  }

  return result;
};

const removeOnePerUuid = (
  coupons: AppliedCoupon[],
  targetUuids: string[],
): AppliedCoupon[] => {
  let result = [...coupons]; // コピー

  for (const uuid of targetUuids) {
    const index = result.findIndex((coupon) => coupon.uuid === uuid);

    if (index !== -1) {
      result = [...result.slice(0, index), ...result.slice(index + 1)];
    }
  }

  return result;
};
